import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { startSubscribe } from "@/lib/subscription";
import { useAuth } from "@/contexts/AuthContext";

import { translateFr as tfr } from "@/i18n";
const errMsg = (e, fb) => {
  const d = e?.response?.data?.detail;
  return (d && (d.message || (typeof d === "string" ? d : null))) || fb;
};

/** Shared plan-change flow: catalogue load, selection, preview, confirm, cancel-scheduled. */
export default function usePlanChange({ onChanged } = {}) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [selected, setSelected] = useState(null);
  const [preview, setPreview] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [enterpriseOpen, setEnterpriseOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/billing/plans");
      setData(r.data);
      setLoadError(false);
    } catch (_) {
      setLoadError(true);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const plans = useMemo(() => data?.plans || [], [data]);
  const current = data?.current_plan_id;
  const subState = data?.subscription?.state;
  const isTrial = !["active", "past_due", "canceled_active"].includes(subState || "");
  const paymentLinks = useMemo(() => data?.payment_links || {}, [data]);
  const currentPlan = useMemo(() => plans.find((p) => p.id === current) || null, [plans, current]);
  const selectedPlan = useMemo(() => plans.find((p) => p.id === selected) || null, [plans, selected]);

  // Plans a trial user cannot pay for yet (missing/unverified Stripe Payment Link)
  const unavailablePlans = useMemo(() => {
    if (!isTrial) return [];
    return plans.filter((p) => !p.contact_sales && !paymentLinks[p.id]).map((p) => p.id);
  }, [plans, paymentLinks, isTrial]);

  const changeMode = useMemo(() => {
    if (!selectedPlan || !currentPlan) return null;
    if (isTrial) return "trial";
    const a = currentPlan.prices?.monthly?.amount || 0;
    const b = selectedPlan.prices?.monthly?.amount || 0;
    return b > a ? "upgrade" : b < a ? "downgrade" : "same";
  }, [selectedPlan, currentPlan, isTrial]);

  const selectPlan = useCallback((id) => {
    const p = plans.find((x) => x.id === id);
    if (!p || p.contact_sales) return;
    if (isTrial && !paymentLinks[id]) return;
    setSelected(id === current ? null : id);
  }, [plans, current, isTrial, paymentLinks]);

  const openConfirm = useCallback(async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const r = await api.post("/billing/preview-change", { plan_id: selected });
      setPreview(r.data);
      setConfirmOpen(true);
    } catch (e) {
      toast.error(errMsg(e, tfr("Aperçu indisponible")));
    } finally {
      setBusy(false);
    }
  }, [selected]);

  const confirmChange = useCallback(async () => {
    setBusy(true);
    try {
      const r = await api.post("/billing/select-plan", { plan_id: selected });
      const msgs = {
        trial_selected: tfr("Offre enregistrée — elle démarrera à l'activation de votre abonnement."),
        upgraded: tfr("Votre offre a été mise à niveau. La proration est gérée par Stripe."),
        downgrade_scheduled: tfr("Changement programmé à la fin de la période de facturation. Vos données sont conservées."),
        current: tfr("Vous êtes déjà sur cette offre."),
        demo: tfr("Mode démo : aucune modification Stripe réelle."),
      };
      toast.success(msgs[r.data.mode] || tfr("Offre mise à jour"));
      setConfirmOpen(false);
      setSelected(null);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(errMsg(e, tfr("Le changement n'a pas pu être appliqué")));
    } finally {
      setBusy(false);
    }
  }, [selected, load, onChanged]);

  const activateNow = useCallback(async (explicitPlanId) => {
    // Checkout target is always explicit: clicked/selected plan, or the CURRENT plan.
    // Never a stale stored selection.
    const target = explicitPlanId || selected || current;
    setBusy(true);
    try {
      if (selected && selected === target) await api.post("/billing/select-plan", { plan_id: selected });
      const res = await startSubscribe({ planId: target, user });
      if (res?.demo) {
        toast.success(res.message || tfr("Mode démo : aucun paiement réel ne sera effectué."));
        setBusy(false);
      }
    } catch (e) {
      toast.error(errMsg(e, tfr("Impossible de démarrer le paiement")));
      setBusy(false);
    }
  }, [selected, current, user]);

  const cancelScheduled = useCallback(async () => {
    setBusy(true);
    try {
      await api.post("/billing/cancel-change");
      toast.success(tfr("Changement programmé annulé — vous conservez votre offre actuelle."));
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(errMsg(e, "Erreur"));
    } finally {
      setBusy(false);
    }
  }, [load, onChanged]);

  return {
    data, plans, loadError, load,
    current, currentPlan, selected, selectedPlan, selectPlan,
    clearSelection: () => setSelected(null),
    changeMode, isTrial, paymentLinks, unavailablePlans,
    preview, confirmOpen, setConfirmOpen,
    enterpriseOpen, setEnterpriseOpen,
    busy, openConfirm, confirmChange, activateNow, cancelScheduled,
  };
}
